import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import { Header, createColumnHelper } from "@tanstack/react-table";
import { useCallback } from "react";
import { ButtonGroup, ButtonToolbar, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { BillingMinimum } from "../../../api/src/billing/lib";
import type { UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import MultiSelector from "../components/MultiSelector";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import { multiSelectIncludes } from "../components/Table/filters";
import { authenticatedFetch } from "../lib/api";
import { formatCurrency, formatPercent } from "../lib/numbers";
import BillingNav from "./BillingNav";
import {
  DELETE_BILLING_MINIMUMS_MUTATION_KEY,
  useQueryBillingMinimums,
} from "./lib";

type BillingMinimumRow = BillingMinimum;

const columnHelper = createColumnHelper<BillingMinimumRow>();

function valueTypeDisplay(valueType: "F" | "P") {
  return valueType === "F" ? "Flat dollar amount" : "Percentage of AUM";
}

export const valueTypeFilterOptions = [
  { label: "Flat dollar amount", value: 1 },
  { label: "Percentage of AUM", value: 2 },
];

const ValueTypeFilter = <TRow, TValue>({
  header,
}: {
  header: Header<TRow, TValue>;
}) => {
  const onChange = useCallback(
    (values: number[]) => {
      header.column.setFilterValue(values);
    },
    [header.column],
  );

  return (
    <MultiSelector
      options={valueTypeFilterOptions}
      placeholder="All"
      setValue={onChange}
    />
  );
};

const columns = [
  columnHelper.display({
    id: "select",
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.name, {
    id: "name",
    cell: (info) => (
      <Link to={`/billing/billing-minimums/${info.row.original.id}`}>
        {info.getValue()}
      </Link>
    ),
    header: () => "Name",
  }),
  columnHelper.accessor((row) => row.value, {
    id: "value",
    cell: (info) =>
      info.row.original.valueType === "P"
        ? formatPercent(+info.getValue() / 100, 2)
        : formatCurrency(+info.getValue()),
    header: () => "Value",
    enableColumnFilter: false,
  }),
  columnHelper.accessor(
    (row) => {
      const option = valueTypeFilterOptions.find(
        (a) => a.label === valueTypeDisplay(row.valueType ?? ""),
      );
      return option?.value.toString() ?? "";
    },
    {
      id: "valueType",
      cell: (info) => {
        const option = valueTypeFilterOptions.find(
          (a) => a.value.toString() === info.getValue(),
        );
        return option?.label ?? "";
      },
      header: () => "Value Type",
      filterFn: multiSelectIncludes,
      meta: {
        filterComponent: ValueTypeFilter,
      },
    },
  ),
];

const BillingMinimumTable = () => {
  const { isPending, data } = useQueryBillingMinimums();

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: data?.data ?? [],
  });

  const { getAccessTokenSilently } = useAuth0();

  const { setError, resetError, errorMessage } = useErrorMessage();

  // One `DELETE /v1/billing/billing-minimums/:id` per selected row (APP-1651:
  // the bulk `POST /billing/billing-minimums/delete-many` is removed).
  const removeBillingMinimums = (ids: number[]) =>
    Promise.allSettled(
      ids.map((id) =>
        authenticatedFetch<
          UnpackResponse<BillingController["deleteBillingMinimum"]>
        >(
          `${process.env.REACT_APP_API_ROOT}/v1/billing/billing-minimums/${id}`,
          getAccessTokenSilently,
          { method: "DELETE" },
        )(),
      ),
    );

  // `useQueryBillingMinimums` walks the list again when this settles
  // (`DELETE_BILLING_MINIMUMS_MUTATION_KEY`), after a partial failure too: a
  // failed delete does not make it settle as an error. The selection is
  // cleared then as well: it holds row indexes, and the rows that are left take
  // new indexes when the list is read again.
  const del = useMutation({
    mutationKey: DELETE_BILLING_MINIMUMS_MUTATION_KEY,
    mutationFn: async () => {
      if (Object.keys(rowSelection).length === 0) return;
      resetError();
      const results = await removeBillingMinimums(
        data?.data.reduce((result, billingMinimum, idx) => {
          return Object.keys(rowSelection).includes(`${idx}`)
            ? [...result, billingMinimum.id]
            : result;
        }, [] as number[]) ?? [],
      );
      setRowSelection({});
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        const message = "Failed to delete billing minimums";
        setError(message);
        console.error(message, failed);
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={BillingNav}>
      {errorMessage}
      <Row>
        <Col className="d-flex justify-content-between mb-3">
          <ButtonToolbar className="gap-3">
            <ButtonGroup>
              <ActionButton
                as={Link}
                to="new"
                variant="secondary"
                label="Create"
                icon="/icons/new.svg"
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Delete"
                icon="/icons/trash.svg"
                onClick={() => del.mutate()}
                disabled={
                  del.isPending || Object.keys(rowSelection).length === 0
                }
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>{isPending ? <Loading /> : <Table table={table} />}</Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default BillingMinimumTable;
